import { useState } from 'react';
import { motion } from 'motion/react';
import { useQuizlets } from '@/queries/quizlets';
import { useUserTier } from '@/queries/subscriptions';
import { CircularLoader } from './CircularLoader';
import type { DeckGroup, CreateDeckGroupRequest, UpdateDeckGroupRequest } from '@/types/deck-group';
import { DECK_GROUP_TIER_LIMITS } from '@/types/deck-group';
import { TIER_NAMES } from '@/types/subscription';
import styles from './DeckGroupForm.module.scss';

interface DeckGroupFormProps {
  initialData?: DeckGroup;
  onSave: (data: CreateDeckGroupRequest | UpdateDeckGroupRequest) => Promise<void>;
  onCancel: () => void;
  isLoading?: boolean;
  error?: string;
}

export function DeckGroupForm({
  initialData,
  onSave,
  onCancel,
  isLoading = false,
  error,
}: DeckGroupFormProps) {
  const tier = useUserTier();
  const tierLimits = DECK_GROUP_TIER_LIMITS[tier];
  const maxQuizzes = tierLimits.maxQuizzesPerGroup;

  const [name, setName] = useState(initialData?.name || '');
  const [description, setDescription] = useState(initialData?.description || '');
  const [selectedQuizletIds, setSelectedQuizletIds] = useState<string[]>(
    initialData?.quizlets.map((q) => q.quizlet_id) || []
  );
  const [searchTerm, setSearchTerm] = useState('');

  const { data: quizlets, isLoading: quizletsLoading } = useQuizlets();

  // Filter quizlets by search term
  const filteredQuizlets = quizlets?.filter((q) =>
    q.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const isAtLimit = maxQuizzes !== null && selectedQuizletIds.length >= maxQuizzes;
  const exceedsLimit = maxQuizzes !== null && selectedQuizletIds.length > maxQuizzes;

  const handleToggleQuizlet = (quizletId: string) => {
    setSelectedQuizletIds((prev) => {
      if (prev.includes(quizletId)) {
        return prev.filter((id) => id !== quizletId);
      }
      // Don't add if at limit
      if (maxQuizzes !== null && prev.length >= maxQuizzes) {
        return prev;
      }
      return [...prev, quizletId];
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!name.trim()) {
      return;
    }

    if (exceedsLimit) {
      return;
    }

    await onSave({
      name: name.trim(),
      description: description.trim() || undefined,
      quizlet_ids: selectedQuizletIds,
    });
  };

  const isFormValid = name.trim().length > 0 && !exceedsLimit;

  return (
    <form className={styles.deckGroupForm} onSubmit={handleSubmit}>
      <h1 className={styles.deckGroupForm__title}>
        {initialData ? 'Edit Deck Group' : 'Create Deck Group'}
      </h1>

      {error && (
        <div className={styles.deckGroupForm__error}>
          {error}
        </div>
      )}

      <div className={styles.deckGroupForm__field}>
        <label className={styles.deckGroupForm__label} htmlFor="name">
          Group Name *
        </label>
        <input
          id="name"
          type="text"
          className={styles.deckGroupForm__input}
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Enter a name for this group"
          required
        />
      </div>

      <div className={styles.deckGroupForm__field}>
        <label className={styles.deckGroupForm__label} htmlFor="description">
          Description
        </label>
        <textarea
          id="description"
          className={styles.deckGroupForm__textarea}
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          placeholder="Optional description of what this group covers"
          rows={3}
        />
      </div>

      <div className={styles.deckGroupForm__section}>
        <div className={styles.deckGroupForm__sectionHeader}>
          <h2 className={styles.deckGroupForm__sectionTitle}>Select Decks</h2>
          <div className={styles.deckGroupForm__limit}>
            <span
              className={`${styles.deckGroupForm__limitCount} ${exceedsLimit ? styles['deckGroupForm__limitCount--exceeded'] : ''}`}
            >
              {selectedQuizletIds.length}
            </span>
            <span className={styles.deckGroupForm__limitSeparator}>/</span>
            <span className={styles.deckGroupForm__limitMax}>
              {maxQuizzes === null ? '∞' : maxQuizzes}
            </span>
            <span className={styles.deckGroupForm__limitLabel}>decks</span>
          </div>
        </div>

        {isAtLimit && !exceedsLimit && (
          <div className={styles.deckGroupForm__warning}>
            You've reached the maximum of {maxQuizzes} decks for your {TIER_NAMES[tier]} plan.
          </div>
        )}

        {exceedsLimit && (
          <div className={styles.deckGroupForm__warningError}>
            This group has more decks than your {TIER_NAMES[tier]} plan allows ({maxQuizzes} max).
            Please remove {selectedQuizletIds.length - maxQuizzes} deck(s) before saving.
          </div>
        )}

        <input
          type="search"
          className={styles.deckGroupForm__search}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search your decks..."
        />

        {quizletsLoading ? (
          <div className={styles.deckGroupForm__loading}>
            <CircularLoader />
          </div>
        ) : filteredQuizlets && filteredQuizlets.length > 0 ? (
          <div className={styles.deckGroupForm__quizletList}>
            {filteredQuizlets.map((quizlet) => {
              const isSelected = selectedQuizletIds.includes(quizlet.id);
              const isDisabled = !isSelected && isAtLimit;

              return (
                <motion.div
                  key={quizlet.id}
                  className={`${styles.deckGroupForm__quizletItem} ${isSelected ? styles['deckGroupForm__quizletItem--selected'] : ''} ${isDisabled ? styles['deckGroupForm__quizletItem--disabled'] : ''}`}
                  onClick={() => !isDisabled && handleToggleQuizlet(quizlet.id)}
                  whileHover={!isDisabled ? { scale: 1.01 } : undefined}
                  whileTap={!isDisabled ? { scale: 0.99 } : undefined}
                >
                  <div className={styles.deckGroupForm__checkbox}>
                    {isSelected && <span className={styles.deckGroupForm__checkmark}>✓</span>}
                  </div>
                  <div className={styles.deckGroupForm__quizletInfo}>
                    <span className={styles.deckGroupForm__quizletName}>{quizlet.name}</span>
                    <span className={styles.deckGroupForm__quizletMeta}>
                      {quizlet.questions?.length || 0} questions
                    </span>
                  </div>
                </motion.div>
              );
            })}
          </div>
        ) : (
          <div className={styles.deckGroupForm__empty}>
            {searchTerm ? 'No decks match your search' : 'No decks available'}
          </div>
        )}
      </div>

      <div className={styles.deckGroupForm__actions}>
        <button
          type="button"
          className={`${styles.deckGroupForm__button} ${styles['deckGroupForm__button--secondary']}`}
          onClick={onCancel}
          disabled={isLoading}
        >
          Cancel
        </button>
        <button
          type="submit"
          className={`${styles.deckGroupForm__button} ${styles['deckGroupForm__button--primary']}`}
          disabled={!isFormValid || isLoading}
        >
          {isLoading ? 'Saving...' : initialData ? 'Save Changes' : 'Create Group'}
        </button>
      </div>
    </form>
  );
}
